import axios from 'axios';
import {
  CreateFeedbackBody, CreatePersonaBody,
  CreatePersonaProfileBody,
  CreatePersonaResponse,
  CreatePersonaSessionResponse,
  CreateSwapResponse,
  GetAllPersona,
  PaginationResults, PersonaProfileResponse,
  PersonaSessionResponse,
  SubscriptionPlansResponse,
  SwapCountInProgressResponse,
  SwapResponse,
  UpdatePersonaProfileBody,
  UpdatePersonaSessionStatusResponse, UpdateSwapBody, UpdateSwapStatusBody, UpdateSwapStatusResponse,
  UpdateUserBody,
  UserResponse, UserStatsResponse,
} from './types';
import { toast } from 'react-toastify';
import { CreateSwapsParams } from '../hooks/api-hooks';

const USERS_ROUTE = `/api/users`;
const PERSONAS_ROUTE = `/api/personas`;
const SWAPS_ROUTE = `/api/swaps`;
const SUBSCRIPTIONS_ROUTE = `/api/subscriptions`;
const FEEDBACK_ROUTE = `/api/feedback`;

export const getUser = async (): Promise<UserResponse> => {
  try {
    const response = await axios.get(`${USERS_ROUTE}/me`);
    return response.data;
  } catch (err) {
    if (err instanceof Error) {
      throw new Error(err.message);
    } else {
      toast.error('An unknown error occurred.');
      throw new Error('An unknown error occurred.');
    }
  }
};

export const getUserStats = async (): Promise<UserStatsResponse[]> => {
  try {
    const response = await axios.get(`${USERS_ROUTE}/me/stats`);
    return response.data;
  } catch (err) {
    if (err instanceof Error) {
      throw new Error(err.message);
    } else {
      toast.error('An unknown error occurred.');
      throw new Error('An unknown error occurred.');
    }
  }
};

export const updateUser = async (updateBody: UpdateUserBody): Promise<UserResponse> => {
  try {
    const response = await axios.patch(`${USERS_ROUTE}/me`, updateBody);
    return response.data;
  } catch (err) {
    if (err instanceof Error) {
      throw new Error(err.message);
    } else {
      toast.error('An unknown error occurred.');
      throw new Error('An unknown error occurred.');
    }
  }
};

export const updateSwap = async (swapId: string, updateSwap: UpdateSwapBody): Promise<SwapResponse> => {
  try {
    const response = await axios.patch(`${SWAPS_ROUTE}/${swapId}`, updateSwap);
    return response.data;
  } catch (err) {
    if (err instanceof Error) {
      throw new Error(err.message);
    } else {
      toast.error('An unknown error occurred.');
      throw new Error('An unknown error occurred.');
    }
  }
};

export const getCurrentPersonaSession = async (): Promise<PersonaSessionResponse> => {
  try {
    const response = await axios.get(`${PERSONAS_ROUTE}/sessions/current`);
    return response.data;
  } catch (err) {
    if (err instanceof Error) {
      toast.error(err.message);
      throw new Error(err.message);
    } else {
      toast.error('An unknown error occurred.');
      throw new Error('An unknown error occurred.');
    }
  }
};

export const createPersonaSession = async (body: { contentType: string }): Promise<CreatePersonaSessionResponse> => {
  try {
    const response = await axios.post(`${PERSONAS_ROUTE}/sessions`, body);
    return response.data;
  } catch (err) {
    if (err instanceof Error) {
      toast.error(err.message);
      throw new Error(err.message);
    } else {
      toast.error('An unknown error occurred.');
      throw new Error('An unknown error occurred.');
    }
  }
};

export type UpdatePersonaSessionBody = {
  status: string;
  failureReason?: string;
}
export const updatePersonaSessionStatus = async (sessionId: string, updateBody: UpdatePersonaSessionBody): Promise<UpdatePersonaSessionStatusResponse> => {
  try {
    const response = await axios.put(`${PERSONAS_ROUTE}/sessions/${sessionId}/status`, updateBody);
    return response.data;
  } catch (err) {
    if (err instanceof Error) {
      toast.error(err.message);
      throw new Error(err.message);
    } else {
      toast.error('An unknown error occurred.');
      throw new Error('An unknown error occurred.');
    }
  }
};

export const createPersona = async ({ candidateId, profileId }: CreatePersonaBody): Promise<CreatePersonaResponse> => {
  try {
    const response = await axios.post(`${PERSONAS_ROUTE}`, { candidateId, profileId });
    return response.data;
  } catch (err) {
    if (err instanceof Error) {
      toast.error(err.message);
      throw new Error(err.message);
    } else {
      toast.error('An unknown error occurred.');
      throw new Error('An unknown error occurred.');
    }
  }
};

export const createSwap = async (createSwapBody: CreateSwapsParams): Promise<CreateSwapResponse[]> => {
  try {
    const response = await axios.post(`${SWAPS_ROUTE}`, createSwapBody);
    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      // Check for a response and use the message in response.data if available
      const errorMessage = err.response?.data?.message || 'An unexpected error occurred';
      toast.error(errorMessage);
      throw new Error(errorMessage);
    } else if (err instanceof Error) {
      toast.error(err.message);
      throw err;
    } else {
      const unknownError = 'An unknown error occurred.';
      toast.error(unknownError);
      throw new Error(unknownError);
    }
  }
};


export const querySwaps = async (
  cursor: string | null = null,
  limit = 25,
  isFavorite?: boolean,
  mediaType?: string[],
): Promise<PaginationResults<SwapResponse>> => {
  try {
    // Start building the URL with base and pagination params
    let url = cursor
      ? `${SWAPS_ROUTE}?${cursor}`
      : SWAPS_ROUTE;

    if (limit) {
      url += cursor ? `&limit=${limit}` : `?limit=${limit}`;
    }

    // Append isFavorite filter if provided
    if (isFavorite) {
      url += `${url.includes('?') ? '&' : '?'}isFavorite=${isFavorite}`;
    }

    // Append mediaType filter if provided and it’s an array
    if (mediaType && mediaType.length > 0) {
      const mediaTypeParams = mediaType.map(type => `mediaType=${type}`).join('&');
      url += `${url.includes('?') ? '&' : '?'}${mediaTypeParams}`;
    }

    const response = await axios.get(url);
    return response.data;
  } catch (err) {
    if (err instanceof Error) {
      toast.error(err.message);
      throw new Error(err.message);
    } else {
      toast.error('An unknown error occurred.');
      throw new Error('An unknown error occurred.');
    }
  }
};

export const deleteSwap = async (swapId: string): Promise<void> => {
  try {
    await axios.delete(`${SWAPS_ROUTE}/${swapId}`);
  } catch (err) {
    if (err instanceof Error) {
      toast.error(err.message);
      throw new Error(err.message);
    } else {
      toast.error('An unknown error occurred.');
      throw new Error('An unknown error occurred.');
    }
  }
};

export const getSwapCountInProgress = async (): Promise<SwapCountInProgressResponse> => {
  try {
    const response = await axios.get(`${SWAPS_ROUTE}/status`);
    return response.data;
  } catch (err) {
    if (err instanceof Error) {
      toast.error(err.message);
      throw new Error(err.message);
    } else {
      toast.error('An unknown error occurred.');
      throw new Error('An unknown error occurred.');
    }
  }
};

export const getSubscriptionPlans = async (): Promise<SubscriptionPlansResponse[]> => {
  try {
    const response = await axios.get(`${SUBSCRIPTIONS_ROUTE}/plans`);
    return response.data;
  } catch (err) {
    if (err instanceof Error) {
      toast.error(err.message);
      throw new Error(err.message);
    } else {
      toast.error('An unknown error occurred.');
      throw new Error('An unknown error occurred.');
    }
  }
};

export const deleteData = async (): Promise<void> => {
  try {
    await axios.delete(`${USERS_ROUTE}/me/data`);
    toast.success('Successfully deleted data');
  } catch (err) {
    if (err instanceof Error) {
      toast.error(err.message);
      throw new Error(err.message);
    } else {
      toast.error('An unknown error occurred.');
      throw new Error('An unknown error occurred.');
    }
  }
};

export const downloadSwap = async (swapId: string): Promise<string> => {
  try {
    const response = await axios.get(`${SWAPS_ROUTE}/${swapId}/download`);
    return response.data;
  } catch (err) {
    if (err instanceof Error) {
      toast.error(err.message);
      throw new Error(err.message);
    } else {
      toast.error('An unknown error occurred.');
      throw new Error('An unknown error occurred.');
    }
  }
};

export const updateSwapStatus = async (swapId: string, updateBody: UpdateSwapStatusBody): Promise<UpdateSwapStatusResponse> => {
  try {
    const response = await axios.put(`${SWAPS_ROUTE}/${swapId}/status`, updateBody);
    return response.data;
  } catch (err) {
    if (err instanceof Error) {
      toast.error(err.message);
      throw new Error(err.message);
    } else {
      toast.error('An unknown error occurred.');
      throw new Error('An unknown error occurred.');
    }
  }
};

export const createFeedback = async (body: CreateFeedbackBody[]): Promise<{ message: string }> => {
  try {
    const response = await axios.post(`${FEEDBACK_ROUTE}`, body);
    toast.success(response.data.message);
    return response.data;
  } catch (err) {
    if (err instanceof Error) {
      toast.error(err.message);
      throw new Error(err.message);
    } else {
      toast.error('An unknown error occurred.');
      throw new Error('An unknown error occurred.');
    }
  }
};

export const getPersonas = async (): Promise<GetAllPersona[]> => {
  try {
    const response = await axios.get(PERSONAS_ROUTE);
    return response.data;
  } catch (err) {
    if (err instanceof Error) {
      toast.error(err.message);
      throw new Error(err.message);
    } else {
      toast.error('An unknown error occurred.');
      throw new Error('An unknown error occurred.');
    }
  }
};

export const getPersonaProfiles = async (): Promise<PersonaProfileResponse[]> => {
  try {
    const response = await axios.get(`${PERSONAS_ROUTE}/profiles`);
    return response.data;
  } catch (err) {
    if (err instanceof Error) {
      toast.error(err.message);
      throw new Error(err.message);
    } else {
      toast.error('An unknown error occurred.');
      throw new Error('An unknown error occurred.');
    }
  }
};

export const createPersonaProfile = async (body: CreatePersonaProfileBody): Promise<PersonaProfileResponse> => {
  try {
    const response = await axios.post(`${PERSONAS_ROUTE}/profiles`, body);
    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      // Handle API error responses (400, etc)
      if (err.response?.data?.message) {
        toast.error(err.response.data.message);
        throw new Error(err.response.data.message);
      }
      // Handle other axios errors
      toast.error(err.message);
      throw new Error(err.message);
    } else {
      // Handle non-axios errors
      toast.error('An unknown error occurred.');
      throw new Error('An unknown error occurred.');
    }
  }
};

export const updatePersonaProfile = async (
  profileId: string,
  body: UpdatePersonaProfileBody
): Promise<PersonaProfileResponse> => {
  try {
    const response = await axios.patch(`${PERSONAS_ROUTE}/profiles/${profileId}`, body);
    return response.data;
  } catch (err) {
    if (err instanceof Error) {
      toast.error(err.message);
      throw new Error(err.message);
    } else {
      toast.error('An unknown error occurred.');
      throw new Error('An unknown error occurred.');
    }
  }
};

export const deletePersonaProfile = async (
  profileId: string,
): Promise<void> => {
  try {
    await axios.delete(`${PERSONAS_ROUTE}/profiles/${profileId}`);
  } catch (err) {
    if (axios.isAxiosError(err)) {
      // Handle API error responses (400, etc)
      if (err.response?.data?.message) {
        toast.error(err.response.data.message);
        throw new Error(err.response.data.message);
      }
      // Handle other axios errors
      toast.error(err.message);
      throw new Error(err.message);
    } else {
      // Handle non-axios errors
      toast.error('An unknown error occurred.');
      throw new Error('An unknown error occurred.');
    }
  }
};